import {
  type PermissionQuery,
  useChurchSignInAddresses,
  useEntitlements,
  useHandoff,
  useOnChurchHost,
  useOpenChurchApp,
  usePermissions,
  useSession,
  useTenant,
} from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { notifications } from "@mantine/notifications";
import {
  IconArrowsExchange,
  IconCalendarUser,
  IconShieldLock,
  IconUser,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { useLocation } from "react-router";
import { addressSwitch } from "@how/tenancy";
import { AppFrame, ShellBrand, type AppFrameAccountLink } from "@how/ui";
import { useInboxUnreadCount } from "../features/communications/inbox-queries";
import { useOwnProfile, useTenantRecord } from "../features/tenant/queries";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";
import { NAV, NAV_CHECKS } from "./nav";

const PORTAL_CHECK: readonly PermissionQuery[] = [
  { permission: "portal.access", module: "member_portal" },
];

/**
 * The staff dashboard shell: AppFrame with the church name and permission-gated navigation. No church switcher: a
 * staff login reaches one church (ADR 0068).
 */
export function Shell() {
  const session = useSession();
  const tenant = useTenantRecord();
  const profile = useOwnProfile();
  const entitlements = useEntitlements();
  const permissions = usePermissions(NAV_CHECKS);
  const unread = useInboxUnreadCount(permissions.allowed.has("communications.inbox"));
  // "Open my member portal": only for someone who holds portal access in this church. issue_handoff checks portal
  // access again; the menu hiding it is not the boundary.
  const portal = usePermissions(PORTAL_CHECK);
  const resolved = useTenant();
  const church = resolved.status === "resolved" ? resolved.tenant : null;
  // The church's live addresses and its preference (R9, R10); HoW's address alone until they are read.
  const addresses = useChurchSignInAddresses(church?.tenantId ?? null);
  const onChurchHost = useOnChurchHost();
  const { pathname, search } = useLocation();
  const handoff = useHandoff();
  const openApp = useOpenChurchApp(env);
  // One notice per failure: each hand-off has its own effect, keyed on its own error, so a second, different
  // failure is told too.
  useEffect(() => {
    if (handoff.error !== null) showOpenFailed();
  }, [handoff.error]);
  useEffect(() => {
    if (openApp.error !== null) showOpenFailed();
  }, [openApp.error]);
  const email = session.status === "signed_in" ? (session.session.user.email ?? "") : "";
  const items = NAV.filter(
    (item) =>
      (item.permission === undefined || permissions.allowed.has(item.permission)) &&
      (item.anyOf === undefined || item.anyOf.some((key) => permissions.allowed.has(key))) &&
      (item.module === undefined || entitlements.data?.has(item.module) === true),
  ).map((item) =>
    item.countPermission !== undefined && permissions.allowed.has(item.countPermission)
      ? { ...item, count: unread.data ?? 0, countLabel: "unread" }
      : item,
  );
  // HoW's own links to the portal follow the church's preferred address (R9, ADR 0067 A8). My schedule lives in the
  // portal (ADR 0070 R11), where the person holds portal access and the church has Services; issue_handoff checks
  // portal access only, and the portal's My schedule checks Services.
  const portalLinks: AppFrameAccountLink[] =
    church === null || !portal.allowed.has("portal.access")
      ? []
      : [
          ...(entitlements.data?.has("services") === true
            ? [
                {
                  label: "My schedule",
                  icon: IconCalendarUser,
                  onSelect: () => {
                    openApp.open({
                      tenantId: church.tenantId,
                      slug: church.slug,
                      app: "member_portal",
                      path: "/my-schedule",
                    });
                  },
                },
              ]
            : []),
          {
            label: "Open my member portal",
            icon: IconUser,
            onSelect: () => {
              openApp.open({
                tenantId: church.tenantId,
                slug: church.slug,
                app: "member_portal",
                path: "/",
              });
            },
          },
        ];
  // "Open at …" (R10): this page on the dashboard's other address, only by a click, through the hand-off.
  const move =
    church === null
      ? null
      : addressSwitch(
          addresses,
          church.slug,
          "staff_dashboard",
          env.VITE_HOW_PLATFORM_BASE_DOMAIN,
          onChurchHost,
          `${pathname}${search}`,
        );
  const moveLinks: AppFrameAccountLink[] =
    move === null
      ? []
      : [
          {
            label: move.label,
            ...(move.description === null ? {} : { description: move.description }),
            icon: IconArrowsExchange,
            onSelect: () => {
              handoff.open(move.target.host, move.target.path);
            },
          },
        ];
  return (
    <AppFrame
      brand={<ShellBrand surface="Staff" churchName={tenant.data?.name} />}
      nav={items}
      account={{
        email,
        ...(profile.data?.display_name ? { name: profile.data.display_name } : {}),
        ...(tenant.data === undefined ? {} : { label: tenant.data.name }),
        // HoW's help center, on the platform's own domain (ADR 0059).
        helpUrl: `https://${env.VITE_HOW_PLATFORM_BASE_DOMAIN}/help`,
        links: [
          ...portalLinks,
          ...moveLinks,
          { to: "/security", label: "Security", icon: IconShieldLock },
        ],
        onSignOut: () => {
          void supabase.auth.signOut();
        },
      }}
    />
  );
}

function showOpenFailed(): void {
  notifications.show({
    color: semanticColor("danger"),
    title: "Could not open that address",
    message: "That address could not be opened. Try again.",
  });
}
